/**
 * demand-filters — search + filter state for the Demands page (spec D1, R2/R3, AC2/AC3).
 *
 * Pure: matching, URL/localStorage encoding and decoding. The hook in
 * src/hooks/use-demand-filters.ts persists the encoded form per user, so the page opens
 * the way you left it — including which Demands were expanded.
 *
 * Status defaults to Active. An absent `status` key means that default; `status=all`
 * means "every status" (an explicit empty selection), so clearing the filter survives a
 * reload instead of snapping back to Active.
 */

export const DEMAND_STATUSES = [
  "Proposed",
  "Planning",
  "Active",
  "Completed",
  "Cancelled",
] as const;
export type DemandStatus = (typeof DEMAND_STATUSES)[number];

export interface DemandFilterState {
  search: string;
  /** Empty = every status. */
  statuses: DemandStatus[];
  /** `YYYY-MM`, inclusive; null = unbounded. */
  from: string | null;
  to: string | null;
  /** Demand ids expanded on the page. */
  open: string[];
}

export const DEFAULT_DEMAND_FILTERS: DemandFilterState = {
  search: "",
  statuses: ["Active"],
  from: null,
  to: null,
  open: [],
};

/** The slice of a Demand row the filters read. */
export interface FilterableDemand {
  id: string;
  name: string;
  accountName: string | null;
  status: string | null;
  /** `YYYY-MM-DD` or null. */
  startDate: string | null;
  endDate: string | null;
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function isStatus(v: string): v is DemandStatus {
  return (DEMAND_STATUSES as readonly string[]).includes(v);
}

/**
 * Search: name or Account contains the text (case-insensitive), or the Demand id starts
 * with it. Status: one of the selected. Date range: the Demand's start–end period
 * overlaps From–To by month; a missing end date is open-ended, a missing start date
 * counts as "always started".
 */
export function matchesDemandFilters(d: FilterableDemand, s: DemandFilterState): boolean {
  const q = s.search.trim().toLowerCase();
  if (q) {
    const hit =
      d.name.toLowerCase().includes(q) ||
      (d.accountName ?? "").toLowerCase().includes(q) ||
      d.id.toLowerCase().startsWith(q);
    if (!hit) return false;
  }
  if (
    s.statuses.length > 0 &&
    (d.status === null || !(s.statuses as string[]).includes(d.status))
  ) {
    return false;
  }
  const startMonth = d.startDate?.slice(0, 7) ?? null;
  const endMonth = d.endDate?.slice(0, 7) ?? null;
  if (s.to !== null && startMonth !== null && startMonth > s.to) return false;
  if (s.from !== null && endMonth !== null && endMonth < s.from) return false;
  return true;
}

/** Filters that narrow the list — search, a non-default status set, a date bound. */
export function activeFilterCount(s: DemandFilterState): number {
  let n = 0;
  if (s.search.trim()) n++;
  if (!sameStatuses(s.statuses, DEFAULT_DEMAND_FILTERS.statuses)) n++;
  if (s.from !== null || s.to !== null) n++;
  return n;
}

function sameStatuses(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v));
}

function splitCsv(v: unknown): string[] {
  return typeof v === "string" && v.length > 0 ? v.split(",").filter(Boolean) : [];
}

export function encodeDemandFilters(s: DemandFilterState): Record<string, string> {
  const out: Record<string, string> = {};
  if (s.search.trim()) out.q = s.search.trim();
  if (!sameStatuses(s.statuses, DEFAULT_DEMAND_FILTERS.statuses)) {
    out.status = s.statuses.length === 0 ? "all" : s.statuses.join(",");
  }
  if (s.from !== null) out.from = s.from;
  if (s.to !== null) out.to = s.to;
  if (s.open.length > 0) out.open = s.open.join(",");
  return out;
}

/** Validates every value and ignores unknown keys, so stale saved filters still load. */
export function decodeDemandFilters(search: Record<string, unknown>): DemandFilterState {
  let statuses: DemandStatus[] = [...DEFAULT_DEMAND_FILTERS.statuses];
  if (search.status === "all") statuses = [];
  else if (typeof search.status === "string") {
    const picked = splitCsv(search.status).filter(isStatus);
    if (picked.length > 0) statuses = [...new Set(picked)];
  }
  const month = (v: unknown) => (typeof v === "string" && MONTH.test(v) ? v : null);
  let from = month(search.from);
  let to = month(search.to);
  if (from !== null && to !== null && from > to) [from, to] = [to, from];
  return {
    search: typeof search.q === "string" ? search.q : "",
    statuses,
    from,
    to,
    open: [...new Set(splitCsv(search.open))].slice(0, 20),
  };
}

/** URL / storage keys this filter owns. */
export const DEMAND_FILTER_KEYS = ["q", "status", "from", "to", "open"] as const;
